import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import LinkIcon from "@mui/icons-material/Link";
import EditNoteIcon from "@mui/icons-material/EditNote";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { useQueryClient } from "@tanstack/react-query";
import {
  useGetConversation,
  useUpdateConversation,
  getGetConversationQueryKey,
} from "../../../api/generated/generated";
import type { ConversationDetail } from "../../../api/generated";
import { useStreamChat } from "../../../hooks/useStreamChat";
import { BriefingPanel } from "./BriefingPanel";
import { ChatInput } from "./ChatInput";
import { MessageList } from "./MessageList";
import { QuickActions } from "./QuickActions";
import { ScorePanel } from "./ScorePanel";

interface ConversationViewProps {
  conversationId: string;
  scorePanelOpen: boolean;
  onScorePanelClose: () => void;
  briefingPanelOpen: boolean;
  onBriefingPanelClose: () => void;
}

const INITIAL_PROMPTS: Record<string, string> = {
  pre_call: "Please prepare a pre-call briefing for this prospect.",
  post_call:
    "Please analyze this sales call transcript and provide detailed scoring and feedback.",
};

export function ConversationView({
  conversationId,
  scorePanelOpen,
  onScorePanelClose,
  briefingPanelOpen,
  onBriefingPanelClose,
}: ConversationViewProps) {
  const navigate = useNavigate();
  const { data, isLoading } = useGetConversation(conversationId);
  const { sendMessage, isStreaming, streamingContent, error } =
    useStreamChat(conversationId);
  const initialSentRef = useRef<string | null>(null);
  const queryClient = useQueryClient();

  const [showReassurance, setShowReassurance] = useState(false);

  const updateConv = useUpdateConversation({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({
          queryKey: getGetConversationQueryKey(conversationId),
        }),
    },
  });

  const detail = data?.data as ConversationDetail | undefined;
  const conversation = detail?.conversation;
  const messages = detail?.messages ?? [];

  // "Thinking" = a response is being generated but nothing has streamed yet.
  // Covers both the pre-first-token window (isStreaming, no content) and the
  // brief window after a new conversation loads but before the initial prompt
  // auto-sends — so the empty/suggestions state never flashes.
  const awaitingFirstToken = isStreaming && !streamingContent;
  const awaitingInitialSend =
    !!conversation &&
    messages.length === 0 &&
    !error &&
    !!INITIAL_PROMPTS[conversation.mode];
  const isThinking =
    !streamingContent && !error && (awaitingFirstToken || awaitingInitialSend);

  // Auto-send initial prompt for new conversations (0 messages)
  useEffect(() => {
    if (!conversation || messages.length > 0 || isStreaming) return;
    if (initialSentRef.current === conversationId) return;

    const prompt = INITIAL_PROMPTS[conversation.mode];
    if (prompt) {
      initialSentRef.current = conversationId;
      sendMessage(prompt);
    }
  }, [conversation, messages.length, isStreaming, conversationId, sendMessage]);

  // Poll for analysis scores on post-call conversations
  useEffect(() => {
    if (
      conversation?.mode !== "post_call" ||
      conversation.analysis ||
      isStreaming
    )
      return;
    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: getGetConversationQueryKey(conversationId),
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [
    conversation?.mode,
    conversation?.analysis,
    isStreaming,
    conversationId,
    queryClient,
  ]);

  // Poll for briefing data on pre-call conversations
  useEffect(() => {
    if (
      conversation?.mode !== "pre_call" ||
      conversation.briefing ||
      isStreaming
    )
      return;
    if (messages.length === 0) return; // No messages yet, nothing to extract from
    const interval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: getGetConversationQueryKey(conversationId),
      });
    }, 5000);
    return () => clearInterval(interval);
  }, [
    conversation?.mode,
    conversation?.briefing,
    isStreaming,
    messages.length,
    conversationId,
    queryClient,
  ]);

  // After a longer wait, append a reassurance line to the thinking indicator.
  useEffect(() => {
    if (!isThinking) {
      setShowReassurance(false);
      return;
    }
    const timer = setTimeout(() => setShowReassurance(true), 8000);
    return () => clearTimeout(timer);
  }, [isThinking]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!conversation) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flex: 1,
        }}
      >
        <Typography sx={{
          color: "text.secondary"
        }}>Conversation not found</Typography>
      </Box>
    );
  }

  const showScoresButton = conversation.mode === "post_call";
  const showSuggestions = messages.length === 0 && !isStreaming && !isThinking;
  const thinkingLabel =
    conversation.mode === "post_call"
      ? "Analyzing your call…"
      : "Preparing your briefing…";
  const hasLinkedPrecall = !!conversation.linked_precall_id;
  const hasLinkedPostcalls =
    conversation.linked_postcall_ids &&
    conversation.linked_postcall_ids.length > 0;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
      {/* Linked conversation chips */}
      {(hasLinkedPrecall || hasLinkedPostcalls) && (
        <Box sx={{ px: 2, pt: 1, display: "flex", gap: 1, flexWrap: "wrap" }}>
          {hasLinkedPrecall && (
            <Chip
              icon={<LinkIcon />}
              label="Linked Pre-Call Research"
              size="small"
              variant="outlined"
              color="info"
              onClick={() =>
                navigate(`/chat/${conversation.linked_precall_id}`)
              }
              clickable
            />
          )}
          {hasLinkedPostcalls &&
            conversation.linked_postcall_ids!.map((id) => (
              <Chip
                key={id}
                icon={<LinkIcon />}
                label="Linked Post-Call Analysis"
                size="small"
                variant="outlined"
                color="warning"
                onClick={() => navigate(`/chat/${id}`)}
                clickable
              />
            ))}
        </Box>
      )}

      {/* Per-conversation writing-style override (spec 040) */}
      <Box sx={{ px: 2, pt: 1 }}>
        <Tooltip title="Use your saved writing style for drafts in this conversation. Set your sample and default in Settings → My Coaching Profile.">
          <Chip
            icon={<EditNoteIcon />}
            size="small"
            variant="outlined"
            color={conversation.writing_style_override ? "primary" : "default"}
            label={
              conversation.writing_style_override == null
                ? "Writing style: default"
                : conversation.writing_style_override
                  ? "Writing style: on"
                  : "Writing style: off"
            }
            disabled={updateConv.isPending}
            onClick={() =>
              updateConv.mutate({
                conversationId,
                data: {
                  writing_style_override:
                    conversation.writing_style_override === true ? false : true,
                },
              })
            }
            clickable
          />
        </Tooltip>
      </Box>

      {/* Messages */}
      <MessageList
        messages={messages}
        isStreaming={isStreaming}
        streamingContent={streamingContent}
        isThinking={isThinking}
        thinkingLabel={thinkingLabel}
        thinkingDetail={
          showReassurance
            ? "This can take a moment for longer calls."
            : undefined
        }
      />

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mx: 2, mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Quick research actions for pre-call */}
      {conversation.mode === "pre_call" && messages.length > 0 && (
        <QuickActions
          prospect={conversation.prospect}
          messages={messages}
          onSend={sendMessage}
          disabled={isStreaming}
        />
      )}

      {/* Input */}
      <ChatInput
        onSend={sendMessage}
        disabled={isStreaming}
        showSuggestions={showSuggestions}
      />

      {/* Score Panel */}
      {showScoresButton && (
        <ScorePanel
          open={scorePanelOpen}
          onClose={onScorePanelClose}
          analysis={conversation.analysis ?? null}
        />
      )}

      {/* Briefing Panel */}
      {conversation.mode === "pre_call" && (
        <BriefingPanel
          open={briefingPanelOpen}
          onClose={onBriefingPanelClose}
          briefing={conversation.briefing ?? null}
          prospect={conversation.prospect}
        />
      )}
    </Box>
  );
}
